import { useEffect, useRef, useCallback } from "react";
import { useEventSocketContext } from "../../context/EventSocketContext";
import { useWsProvider } from "../../context/NativeApplicationContext";
import type { StreamerRole } from "../EventSocket/types";

interface UseStreamerSocketProps {
  eventId: string;
  username: string;
  role: StreamerRole;
  accessToken?: string;
}

/**
 * useStreamerSocket - Dedicated hook for streamer socket integration
 *
 * Automatically:
 * - Connects to event socket on mount
 * - Fetches initial event state
 * - Watches NativeApplicationContext state changes
 * - Emits socket events on streaming state transitions
 */
export function useStreamerSocket({
  eventId,
  username,
  role,
  accessToken,
}: UseStreamerSocketProps) {
  const {
    connectToEvent,
    disconnectFromEvent,
    fetchInitialState,
    emitStreamStart,
    emitStreamDisconnect,
    emitStreamReconnect,
    emitStreamStop,
    isConnected,
    organiserStatus,
    challengerStatus,
  } = useEventSocketContext();

  const { nativeAppStatus, streamStarted } = useWsProvider();

  // Track previous states to detect transitions
  const prevStreamStarted = useRef<boolean | null>(null);
  const prevNativeState = useRef<string | null>(null);
  const hasEmittedStart = useRef(false);

  // ============================================
  // Connect to socket on mount
  // ============================================
  useEffect(() => {
    if (!eventId || !username || !role) {
      console.log(
        "[useStreamerSocket] Missing required props, skipping connection"
      );
      return;
    }

    console.log(
      "[useStreamerSocket] Connecting to event:",
      eventId,
      "as",
      role
    );
    connectToEvent(eventId, username, role, accessToken);

    // Fetch initial state
    fetchInitialState(eventId);

    // Cleanup on unmount
    return () => {
      console.log("[useStreamerSocket] Disconnecting from event");
      disconnectFromEvent();
      hasEmittedStart.current = false;
    };
  }, [
    eventId,
    username,
    role,
    accessToken,
    connectToEvent,
    disconnectFromEvent,
    fetchInitialState,
  ]);

  // ============================================
  // Watch for streaming start/stop
  // ============================================
  useEffect(() => {
    const wasStreaming = prevStreamStarted.current;
    const isStreaming = streamStarted;

    // Initialize on first render
    if (wasStreaming === null) {
      prevStreamStarted.current = isStreaming;
      return;
    }

    // Detect start: was not streaming, now is streaming
    if (!wasStreaming && isStreaming && !hasEmittedStart.current) {
      console.log("[useStreamerSocket] Stream STARTED - emitting socket event");
      emitStreamStart(username, role);
      hasEmittedStart.current = true;
    }

    // Detect stop: was streaming, now is not streaming (and we had emitted start)
    if (wasStreaming && !isStreaming && hasEmittedStart.current) {
      console.log("[useStreamerSocket] Stream STOPPED - emitting socket event");
      emitStreamStop(username, role);
      hasEmittedStart.current = false;
    }

    prevStreamStarted.current = isStreaming;
  }, [streamStarted, username, role, emitStreamStart, emitStreamStop]);

  // ============================================
  // Watch for network issues during streaming
  // ============================================
  useEffect(() => {
    const prevState = prevNativeState.current;
    const currentState = nativeAppStatus?.state;

    // Initialize on first render
    if (prevState === null) {
      prevNativeState.current = currentState || null;
      return;
    }

    // Only process if we had emitted start
    if (!hasEmittedStart.current) {
      prevNativeState.current = currentState || null;
      return;
    }

    // States that indicate unexpected disconnection (not graceful stop)
    const disconnectStates = [
      "network-disconnected",
      "network-reconnecting",
      "streaming-error",
      "service-error",
      "service-restarting",
      "restarting-streaming",
    ];

    // States that indicate recovery to streaming
    const recoveryStates = ["network-reconnecting", "restarting-streaming"];

    // Detect disconnect: streaming → any non-idle state (except idle which is graceful stop)
    // Note: streaming → idle is handled in the streamStarted effect as graceful stop
    if (
      prevState === "streaming" &&
      currentState !== "streaming" &&
      currentState !== "idle" &&
      disconnectStates.includes(currentState || "")
    ) {
      console.log(
        "[useStreamerSocket] Network DISCONNECTED - emitting socket event",
        `(${prevState} → ${currentState})`
      );
      emitStreamDisconnect(username, role, currentState || "Unknown");
    }

    // Detect reconnect: recovery state → streaming
    if (
      recoveryStates.includes(prevState || "") &&
      currentState === "streaming"
    ) {
      console.log(
        "[useStreamerSocket] Stream RECONNECTED - emitting socket event",
        `(${prevState} → ${currentState})`
      );
      emitStreamReconnect(username, role);
    }

    prevNativeState.current = currentState || null;
  }, [
    nativeAppStatus?.state,
    username,
    role,
    emitStreamDisconnect,
    emitStreamReconnect,
  ]);

  // ============================================
  // Manual emit functions (for edge cases)
  // ============================================
  const manualEmitStart = useCallback(() => {
    if (!hasEmittedStart.current) {
      emitStreamStart(username, role);
      hasEmittedStart.current = true;
    }
  }, [username, role, emitStreamStart]);

  const manualEmitStop = useCallback(() => {
    if (hasEmittedStart.current) {
      emitStreamStop(username, role);
      hasEmittedStart.current = false;
    }
  }, [username, role, emitStreamStop]);

  const manualEmitDisconnect = useCallback(
    (reason?: string) => {
      emitStreamDisconnect(username, role, reason);
    },
    [username, role, emitStreamDisconnect]
  );

  const manualEmitReconnect = useCallback(() => {
    emitStreamReconnect(username, role);
  }, [username, role, emitStreamReconnect]);

  // ============================================
  // Return values
  // ============================================
  return {
    // Connection state
    isConnected,

    // Stream status (from socket)
    organiserStatus,
    challengerStatus,

    // Opponent status (derived)
    opponentStatus: role === "organiser" ? challengerStatus : organiserStatus,
    isOpponentLive:
      (role === "organiser" ? challengerStatus : organiserStatus) === "live",
    isOpponentPaused:
      (role === "organiser" ? challengerStatus : organiserStatus) === "paused",

    // Manual emit functions (for edge cases)
    manualEmitStart,
    manualEmitStop,
    manualEmitDisconnect,
    manualEmitReconnect,
  };
}
